/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  TMyHubBadge,
  TMyHubBulkActionType,
  TMyHubType
} from "../../model/types";
import type {
  TCarouselItem,
  TMyHubFolderDetail,
  TMyHubItemUnion,
  TRecordingMyHubItem
} from "../../model/types";
import { Locale, LocaleKeys } from "../../model/locale";
import { CarouselTemplate } from "../../model/enums";
import { DateTime } from "../../model/utility/DateTime";
import { mapMyHubItemToCarouselItem } from "./myHubMappers";
import {
  formatSavedDate,
  formatSavedDuration,
  interpolate,
  toRenderableRowActions
} from "./myHubSavedMappers";
import { MyHubFilterId } from "./myHub.types";
import type {
  TMyHubSavedItem,
  TMyHubSeriesHeading,
  TMyHubSeriesSummary
} from "./myHub.types";

/**
 * Mappers for the manage-series screen (Figma: My Hub Detail — node 71:4516,
 * "Managed recordings -series").
 *
 * The screen's rows are {@link TMyHubSavedItem}, the same shape the Saved and
 * Deleted tabs render, because they are the same row: a landscape thumbnail, a
 * headline, a dated line and an optional accent line. Only the composed copy
 * differs, so this module owns that copy and the view reuses `MyHubSavedList`
 * unchanged.
 *
 * Two differences from `myHubSavedMappers`, both driven by the design:
 *   - the headline drops the series name ("S5 E14 ‧ Life is a Promise"), since
 *     every row on the screen belongs to the series already named in the header;
 *   - the dated line states when the episode aired rather than when it was
 *     saved ("Today  9:00-10:00pm on NBC").
 */

/**
 * Tile template whose pixel dimensions the row thumbnails are requested at.
 *
 * The same plain 16 ∶ 9 landscape art the flat list requests — the two designs
 * share the row, so they share the template. See `SAVED_THUMBNAIL_TEMPLATE`.
 */
const SERIES_THUMBNAIL_TEMPLATE: CarouselTemplate =
  CarouselTemplate.LANDSCAPE_HORIZONTAL;

/** Middot separator between the episode label and the episode title. */
const MIDDOT: string = "‧";

/** Separator between the airing date and its time range — Figma uses spaces. */
const AIRING_SEPARATOR: string = "  ";

/** Joins the two ends of the time range — Figma: "9:00-10:00pm". */
const TIME_RANGE_DASH: string = "-";

/** Meridiem suffixes `DateTime.to12HourClock` can emit. */
const MERIDIEMS: readonly string[] = ["am", "pm"];

/**
 * Formats an airing window as the compact range the rows show: "9:00-10:00pm".
 *
 * The start drops its own meridiem when both ends share one, which is what the
 * design renders and how a time range normally reads; a window that straddles
 * noon or midnight keeps both ("11:30am-1:00pm").
 *
 * @param startTime - ISO 8601 start, possibly absent.
 * @param endTime - ISO 8601 end, possibly absent.
 * @returns The range, or `""` when either end is missing or unparseable.
 */
export const formatAiringTimeRange = (
  startTime: string | undefined,
  endTime: string | undefined
): string => {
  if (
    startTime === undefined ||
    startTime === "" ||
    endTime === undefined ||
    endTime === ""
  ) {
    return "";
  }

  const start: DateTime = new DateTime(startTime);
  const end: DateTime = new DateTime(endTime);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "";
  }

  const startClock: string = start.to12HourClock();
  const endClock: string = end.to12HourClock();

  const sharedMeridiem: string | undefined = MERIDIEMS.find(
    (meridiem: string): boolean =>
      startClock.endsWith(meridiem) && endClock.endsWith(meridiem)
  );

  const startLabel: string =
    sharedMeridiem === undefined
      ? startClock
      : startClock.slice(0, startClock.length - sharedMeridiem.length);

  return startLabel + TIME_RANGE_DASH + endClock;
};

/**
 * Composes a row's dated line — "Today  9:00-10:00pm on NBC", or
 * "4/27/2026  9:00-10:00pm on NBC" once the airing is no longer today.
 *
 * Every part is optional and dropped when the data behind it is missing, so a
 * recording with no station still reads as a date and a time rather than
 * trailing an empty "on".
 *
 * Takes only the airing window, so a mapped tile (the recording highlight's
 * movie card) composes the same line as a wire item.
 *
 * @param item - The recording being mapped, or anything carrying its airing.
 * @param channelCallSign - Call sign for the item's station, or `""` when
 * unknown. Resolved by the caller, which is the only layer that can.
 * @returns The composed line, or `""` when the item carries no airing at all.
 */
export const composeSeriesAiringLabel = (
  item: Pick<TRecordingMyHubItem, "startTime" | "endTime">,
  channelCallSign: string = ""
): string => {
  const parts: string[] = [];

  if (item.startTime !== undefined && item.startTime !== "") {
    const start: DateTime = new DateTime(item.startTime);
    if (!Number.isNaN(start.getTime())) {
      parts.push(
        start.isToday()
          ? Locale.getString(LocaleKeys.MY_HUB_SERIES_AIRED_TODAY)
          : formatSavedDate(item.startTime)
      );
    }
  }

  const timeRange: string = formatAiringTimeRange(item.startTime, item.endTime);
  if (timeRange !== "") {
    parts.push(timeRange);
  }

  if (channelCallSign !== "") {
    parts.push(
      interpolate(
        Locale.getString(LocaleKeys.MY_HUB_SERIES_AIRED_ON_CHANNEL),
        channelCallSign
      )
    );
  }

  // The date and its time range read as one unit, so they are joined by the
  // design's wider gap; the channel clause is a separate phrase and takes a
  // plain space.
  if (parts.length <= 2) {
    return parts.join(AIRING_SEPARATOR);
  }
  return (
    parts.slice(0, 2).join(AIRING_SEPARATOR) + " " + parts.slice(2).join(" ")
  );
};

/**
 * Composes the row headline — "S5 E14 ‧ Life is a Promise".
 *
 * Unlike the flat list's headline this omits the series name: the header above
 * the rows already says which series is open, so repeating it on every row would
 * only push the episode title out of view.
 *
 * @param item - The recording being mapped.
 * @returns The episode line, falling back to the item title when the API sent
 * neither numbering nor an episode title (a special, typically).
 */
export const composeSeriesHeadline = (item: TRecordingMyHubItem): string => {
  const hasNumbering: boolean =
    item.seasonOrYear !== undefined && item.episodeNumber !== undefined;
  const numbering: string = hasNumbering
    ? interpolate(
        Locale.getString(LocaleKeys.CONTENT_ACTION_SUFFIX_SEASON_EPISODE),
        String(item.seasonOrYear),
        String(item.episodeNumber)
      )
    : "";

  const episodeTitle: string = item.episodeTitle || "";

  if (numbering !== "" && episodeTitle !== "") {
    return `${numbering} ${MIDDOT} ${episodeTitle}`;
  }
  if (numbering !== "") {
    return numbering;
  }
  return episodeTitle !== "" ? episodeTitle : item.title;
};

/**
 * Maps one of the folder's recordings onto its presentation-ready row.
 *
 * @param item - A recording from the folder-detail response.
 * @param imageHostBaseUrl - Image host resolved once per fetch.
 * @param channelCallSign - Call sign for the item's station, or `""`.
 * @returns The row the flat list renders.
 */
export const mapMyHubSeriesItem = (
  item: TRecordingMyHubItem,
  imageHostBaseUrl: string = "",
  channelCallSign: string = ""
): TMyHubSavedItem => {
  // Thumbnail resolution and watch progress are already solved for every MyHub
  // item type by the carousel mapper — reuse it rather than re-deriving the
  // image-type rules here. The tile is also carried on the row whole, for the
  // same reason `myHubSavedMappers` carries it: an action acts on the tile.
  const tile: TCarouselItem = mapMyHubItemToCarouselItem(
    item,
    imageHostBaseUrl,
    SERIES_THUMBNAIL_TEMPLATE
  );

  const expiresOnDate: string = formatSavedDate(item.expiresOn);

  // Only a saved episode carries the thumbnail caption ("Saved ‧ 1 hour" —
  // Figma node 71:4748). The caption states why the recording is being kept, so
  // an ordinary recording that will expire on its own has nothing to say there.
  const isSaved: boolean =
    item.badges !== undefined && item.badges.includes(TMyHubBadge.SAVED);
  const durationText: string = isSaved
    ? formatSavedDuration(item.duration)
    : "";

  const labelFor = (key: string, value: string): string =>
    value === "" ? "" : interpolate(Locale.getString(key), value);

  return {
    id: item.myHubItemId,
    recordingId: item.recordingId,
    contentId: item.contentId,
    headline: composeSeriesHeadline(item),
    dateLabel: composeSeriesAiringLabel(item, channelCallSign),
    expiresOnLabel: labelFor(LocaleKeys.MY_HUB_SAVED_EXPIRES_ON, expiresOnDate),
    durationLabel: labelFor(
      LocaleKeys.MY_HUB_SAVED_THUMBNAIL_CAPTION,
      durationText
    ),
    imageUrl: tile.imageUrl,
    progressPercent: tile.progressPercent,
    // Filtered, and copied rather than aliased — the row goes into Redux state
    // and must not share an array with the raw API payload. The filter is not
    // cosmetic: `MyHubSavedList` renders only the actions it has a button for,
    // while the slice reads this array for the row's LEFT/RIGHT bounds and for
    // what OK fires, so an action the list cannot draw has to be gone before
    // the row is stored — see toRenderableRowActions.
    availableActions: toRenderableRowActions(item.availableActions),
    tile
  };
};

/**
 * Applies one of the "{{name}} Recordings" templates and reports the result in
 * both forms the title block needs — see {@link composeSeriesHeading}, which is
 * where the behaviour is documented.
 *
 * @param template - The locale template to fill.
 * @param title - The series or airing name to fill it with.
 * @returns The heading, whole and split into its two spans.
 */
const splitHeading = (template: string, title: string): TMyHubSeriesHeading => {
  // The suffix is whatever the template adds around the name, so the check
  // stays correct when a translation words it differently.
  const suffix: string = interpolate(template, "").trim();
  const alreadySuffixed: boolean =
    suffix !== "" && title.toLowerCase().endsWith(suffix.toLowerCase());

  if (alreadySuffixed) {
    return {
      text: title,
      name: title.slice(0, title.length - suffix.length).trim(),
      suffix
    };
  }

  return {
    text: interpolate(template, title),
    name: title,
    suffix
  };
};

/**
 * The screen's `<h1>`: the folder's name, plus which kind of recordings this
 * drill-in lists — "Yellowstone Recordings" from the Recorded tab, "Yellowstone
 * Scheduled Recordings" from Scheduled.
 *
 * Returned in parts as well as whole, because the heading has to stay on one
 * line: the view renders `name` in a span that ellipsises and `suffix` in one
 * that never shrinks, so a long series name truncates *before* "Recordings"
 * rather than pushing it onto a second line or out of sight.
 *
 * Appends only when the name does not already carry the suffix. `folderTitle`
 * is documented as server-owned and already composed (see TMyHubFolderDetail),
 * but the drill-ins show the bare series name in practice, so the client
 * composes it — and this guard is what keeps the two from colliding into
 * "Yellowstone Recordings Recordings" if a build starts sending the long form.
 * When the service did send the long form, the suffix is split back off so the
 * view still gets the two parts it truncates separately.
 *
 * Both the guard and the split assume the template appends — it is
 * "{{seriesTitle}} Recordings" in every locale shipped today. A translation
 * that *prefixed* the wording would fall through to the unsuffixed branch and
 * render as one unsplittable run, which is the old behaviour rather than a
 * regression.
 *
 * @param folderTitle - The name as the service sent it.
 * @param filter - Which drill-in this is.
 * @returns The heading to render, whole and split into its two spans.
 */
const composeSeriesHeading = (
  folderTitle: string,
  filter: MyHubFilterId
): TMyHubSeriesHeading =>
  splitHeading(
    Locale.getString(
      filter === MyHubFilterId.SCHEDULED
        ? LocaleKeys.MY_HUB_SERIES_TITLE_SCHEDULED
        : LocaleKeys.MY_HUB_SERIES_TITLE_RECORDED
    ),
    folderTitle
  );

/**
 * Maps the folder-detail header onto the screen's title block.
 *
 * @param response - The folder-detail response.
 * @param resolvedCount - Number of rows actually mapped, used when the service
 * omitted `itemCount` so the header agrees with the list beneath it.
 * @param filter - Which drill-in this is, which decides the heading's wording.
 * Defaults to the recorded variant, matching `fetchMyHubSeriesDetail`'s own
 * fallback for any filter the folder endpoint does not distinguish.
 * @returns The header, or `null` when the response carries no title — the view
 * then renders nothing rather than an empty heading.
 */
export const mapMyHubSeriesSummary = (
  response: TMyHubFolderDetail,
  resolvedCount: number = 0,
  filter: MyHubFilterId = MyHubFilterId.RECORDED
): TMyHubSeriesSummary | null => {
  if (response.folderTitle === undefined || response.folderTitle === "") {
    return null;
  }

  const heading: TMyHubSeriesHeading = composeSeriesHeading(
    response.folderTitle,
    filter
  );

  return {
    title: response.folderTitle,
    heading: heading.text,
    headingName: heading.name,
    headingSuffix: heading.suffix,
    // `||` rather than a ternary: `itemCount` is a count, so `0` and `undefined`
    // both mean "nothing the service vouched for" and both fall back to the rows.
    episodeCount: response.itemCount || resolvedCount,
    totalMinutes: response.totalDurationMinutes || 0,
    folderActions: toRenderableFolderActionTypes(response.folderActions)
  };
};

/**
 * Flattens a folder-detail response into the rows and header the screen renders.
 *
 * Non-recording items are dropped: a row needs a `recordingId` to service its
 * actions and an airing to compose its dated line, and a folder that nests
 * another folder has nothing to render for it.
 *
 * @param response - The folder-detail response.
 * @param imageHostBaseUrl - Image host resolved once per fetch.
 * @param resolveCallSign - Turns a `stationId` into a channel call sign. Passed
 * in rather than looked up here so this module stays pure and testable — the
 * wire model carries only the station id.
 * @param filter - Which drill-in this is — see {@link mapMyHubSeriesSummary}.
 * @returns The rows and the header.
 */
export const mapMyHubFolderDetail = (
  response: TMyHubFolderDetail,
  imageHostBaseUrl: string = "",
  resolveCallSign: (stationId: string | undefined) => string = (): string => "",
  filter: MyHubFilterId = MyHubFilterId.RECORDED
): { items: TMyHubSavedItem[]; summary: TMyHubSeriesSummary | null } => {
  const items: TMyHubSavedItem[] = response.items
    .filter(
      (item: TMyHubItemUnion): item is TRecordingMyHubItem =>
        item.myHubItemType === TMyHubType.RECORDING
    )
    .map(
      (item: TRecordingMyHubItem): TMyHubSavedItem =>
        mapMyHubSeriesItem(
          item,
          imageHostBaseUrl,
          resolveCallSign(item.stationId)
        )
    );

  return {
    items,
    summary: mapMyHubSeriesSummary(response, items.length, filter)
  };
};

// ── Header copy ───────────────────────────────────────────────────────────────
// Composed here rather than in the screen for the same reason the row copy is
// (see this module's header): it is derived state, so the View reads the
// finished string instead of deriving it.

/** Separator between the two halves of the subtitle — "12 Episodes | 12 hours". */
const SUBTITLE_SEPARATOR: string = " | ";

const MINUTES_PER_HOUR: number = 60;

/**
 * Labels for the folder buttons the service can offer in the header.
 *
 * A table rather than a switch, so an action the client has no copy for is
 * simply not rendered: the row is server-driven (`folderActions`), and an
 * unlabelled button would be worse than a missing one.
 */
const FOLDER_ACTION_LABEL_KEYS: Partial<Record<TMyHubBulkActionType, string>> =
  {
    [TMyHubBulkActionType.RECORDING_OPTIONS]:
      LocaleKeys.MY_HUB_SERIES_FOLDER_RECORDING_OPTIONS,
    [TMyHubBulkActionType.CANCEL_SERIES_RECORDINGS]:
      LocaleKeys.MY_HUB_SERIES_FOLDER_CANCEL_RECORDINGS,
    [TMyHubBulkActionType.DELETE_ALL_RECORDINGS]:
      LocaleKeys.MY_HUB_SERIES_FOLDER_DELETE_ALL,
    [TMyHubBulkActionType.START_SERIES_RECORDING]:
      LocaleKeys.MY_HUB_SERIES_FOLDER_START_RECORDING
  };

/**
 * The folder actions the header can actually draw a button for, in the
 * service's order.
 *
 * Applied when the summary is mapped, not only when the buttons are rendered,
 * because the stored array is what the D-pad walks: `seriesActionButtonCount`
 * in myHubSlice reads `summary.folderActions.length` for the button row's
 * LEFT/RIGHT bounds and for when RIGHT crosses to the sort control. An action
 * with no label reaching the store would leave a column the cursor can enter
 * and no button to show for it — and one extra RIGHT press before the sort
 * control. The flat list's rows are filtered for the same reason; see
 * `toRenderableRowActions` in myHubSavedMappers.
 *
 * @param folderActions - Actions declared by the response, in its own order.
 * @returns The renderable subset, order preserved.
 */
const toRenderableFolderActionTypes = (
  folderActions: readonly TMyHubBulkActionType[] | undefined
): TMyHubBulkActionType[] => {
  if (folderActions === undefined) {
    return [];
  }
  return folderActions.filter(
    (actionType: TMyHubBulkActionType): boolean =>
      FOLDER_ACTION_LABEL_KEYS[actionType] !== undefined
  );
};

/** One folder button, resolved for rendering. */
export type TMyHubFolderActionButton = {
  /** The API affordance the button fires. */
  actionType: TMyHubBulkActionType;
  /** Localized label, ready to render. */
  label: string;
};

/**
 * Composes the subtitle beneath the screen title — "12 Episodes | 12 hours".
 *
 * Either half is dropped when the response did not supply the figure behind it,
 * so the line never reads "0 Episodes" or "0 hours" as though that were a fact
 * the service vouched for.
 *
 * The scheduled drill-in states no line at all: both figures describe what has
 * been recorded and how much of the allowance it uses, and nothing in a list of
 * future airings has been recorded yet.
 *
 * @param summary - Mapped folder header, or `null` before the response arrives.
 * @param filter - Which drill-in this is — see {@link mapMyHubSeriesSummary}.
 * @returns The composed line, or `""` when there is nothing to state.
 */
export const composeSeriesSubtitle = (
  summary: TMyHubSeriesSummary | null,
  filter: MyHubFilterId = MyHubFilterId.RECORDED
): string => {
  if (summary === null || filter === MyHubFilterId.SCHEDULED) {
    return "";
  }

  const parts: string[] = [];

  if (summary.episodeCount > 0) {
    parts.push(
      interpolate(
        Locale.getString(LocaleKeys.MY_HUB_SERIES_EPISODE_COUNT),
        String(summary.episodeCount)
      )
    );
  }

  if (summary.totalMinutes > 0) {
    // Whole hours, because that is what the design shows and the API reports
    // minutes — the same rounding the flat list's summary line uses.
    const hours: number = Math.round(summary.totalMinutes / MINUTES_PER_HOUR);
    parts.push(
      interpolate(
        Locale.getString(LocaleKeys.MY_HUB_SERIES_TOTAL_HOURS),
        String(hours)
      )
    );
  }

  return parts.join(SUBTITLE_SEPARATOR);
};

/**
 * Resolves the response's folder actions into renderable buttons, dropping any
 * the client has no label for.
 *
 * @param folderActions - Actions declared by the response, in its own order.
 * @returns The renderable subset, order preserved.
 */
export const toRenderableFolderActions = (
  folderActions: readonly TMyHubBulkActionType[]
): TMyHubFolderActionButton[] => {
  return toRenderableFolderActionTypes(folderActions).map(
    (actionType: TMyHubBulkActionType): TMyHubFolderActionButton => {
      // Non-null: the filter above dropped every type without a label key.
      const localeKey: string = FOLDER_ACTION_LABEL_KEYS[actionType] as string;
      return { actionType, label: Locale.getString(localeKey) };
    }
  );
};

// ── Scheduled tab drill-ins ───────────────────────────────────────────────────
// The Scheduled tab opens the same screen this module already serves (Figma:
// My Hub Detail — nodes 407:1437 and 407:1709, both "Scheduled _ series"). A
// scheduled *series* needs nothing new: the folder-detail endpoint's
// `myHubFilter=scheduled` variant supplies the "… Scheduled Recordings" title,
// the scheduled `folderActions` and each row's Detail/Extend affordances, and
// `mapMyHubFolderDetail` above already maps it.
//
// A scheduled *single* recording is the one case with no folder behind it: it
// is offer-keyed, not collection-keyed, so there is nothing to fetch. What the
// screen renders is composed here from the tile the cursor was on.

/**
 * Composes the scheduled-single screen from the tile the user pressed OK on.
 *
 * Returns the same `{ items, summary }` pair {@link mapMyHubFolderDetail}
 * does, so the screen cannot tell a seeded single airing from a fetched
 * folder — which is what lets both Scheduled drill-ins reuse one screen.
 *
 * @param tile - The mapped Scheduled-tab tile the press came from.
 * @param channelCallSign - Call sign for the tile's station, or `""`.
 * @returns The single row and the header the screen renders.
 */
export const mapScheduledSingleRecording = (
  tile: TCarouselItem,
  channelCallSign: string = ""
): { items: TMyHubSavedItem[]; summary: TMyHubSeriesSummary } => {
  // The row copy is composed by the same two helpers the folder's rows use, so
  // a lone scheduled airing reads exactly like an episode inside a series.
  // Both take the wire shape and the tile carries every field they read under
  // the carousel's own names, hence this narrow adaptation rather than a second
  // set of copy rules.
  const airing: TRecordingMyHubItem = {
    myHubItemId: tile.id,
    myHubItemType: TMyHubType.RECORDING,
    title: tile.title,
    contentId: tile.contentId,
    seasonOrYear: tile.seasonNumber,
    episodeNumber: tile.episodeNumber,
    episodeTitle: tile.episodeTitle,
    startTime: tile.startTime,
    endTime: tile.endTime,
    stationId: tile.stationId
  };

  const row: TMyHubSavedItem = {
    id: tile.id,
    recordingId: tile.recordingId,
    contentId: tile.contentId,
    headline: composeSeriesHeadline(airing),
    dateLabel: composeSeriesAiringLabel(airing, channelCallSign),
    // Nothing has been recorded yet, so there is no expiry to state and no
    // "Saved ‧ 1 hour" caption — that caption says why a recording is being
    // kept past its expiry, which cannot apply to a future airing.
    expiresOnLabel: "",
    durationLabel: "",
    imageUrl: tile.imageUrl,
    progressPercent: tile.progressPercent,
    // Filtered, and copied rather than aliased — the row goes into Redux state
    // and must not share an array with the tile it was seeded from. Same reason
    // as the folder rows above: the stored array is what the D-pad walks, so it
    // has to hold exactly the actions the list draws a button for.
    availableActions: toRenderableRowActions(tile.availableActions),
    tile
  };

  // Composed here rather than server-owned: `folderTitle` comes with a folder,
  // and this airing has none. The wording follows the scheduled folder
  // variant's own ("Yellowstone Scheduled Recordings") so the two drill-ins
  // read alike — including how the title block truncates a long name, which is
  // why it goes through the same splitter.
  const heading: TMyHubSeriesHeading = splitHeading(
    Locale.getString(LocaleKeys.MY_HUB_SCHEDULED_SINGLE_TITLE),
    tile.title
  );

  return {
    items: [row],
    summary: {
      title: tile.title,
      heading: heading.text,
      headingName: heading.name,
      headingSuffix: heading.suffix,
      // Both are folder figures. One airing is not a collection, so neither is
      // claimed — `composeSeriesSubtitle` drops a zero rather than printing
      // it, which is also what the design shows (no subtitle line).
      episodeCount: 0,
      totalMinutes: 0,
      // The design's one folder button for this state (node 407:1709). Offered
      // unconditionally: a single scheduled airing is precisely the case where
      // recording the whole series is the useful next step.
      folderActions: [TMyHubBulkActionType.START_SERIES_RECORDING]
    }
  };
};
